import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  formatTimestamp,
  parseTimestamp,
  validateWordTimestamp,
  RIPPLE_MODE
} from '../utils/timestampUtils';
import './TimestampEditor.css';

const TimestampEditor = ({
  word,
  wordIndex,
  segmentIndex,
  globalIndex,
  anchorElement,
  audioRef,
  onSave,
  onCancel,
  previousWord,
  nextWord
}) => {
  const [startValue, setStartValue] = useState(formatTimestamp(word?.start));
  const [endValue, setEndValue] = useState(formatTimestamp(word?.end));
  const [rippleMode, setRippleMode] = useState(RIPPLE_MODE.ADJUST_NEIGHBOR);
  const [errors, setErrors] = useState([]);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPlaying, setIsPlaying] = useState(false);
  const editorRef = useRef(null);
  const startInputRef = useRef(null);
  const playTimeoutRef = useRef(null);

  // Calculate position relative to anchor element
  useEffect(() => {
    if (!anchorElement) return;

    const updatePosition = () => {
      const rect = anchorElement.getBoundingClientRect();
      const editorWidth = 320;
      const editorHeight = 300;

      let top = rect.bottom + 8;
      let left = rect.left;

      // Adjust if too close to right edge
      if (left + editorWidth > window.innerWidth - 20) {
        left = window.innerWidth - editorWidth - 20;
      }

      // Adjust if too close to bottom edge
      if (top + editorHeight > window.innerHeight - 20) {
        top = rect.top - editorHeight - 8;
      }

      // Ensure minimum left position
      if (left < 20) {
        left = 20;
      }

      setPosition({ top, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorElement]);

  // Focus start input on mount
  useEffect(() => {
    if (startInputRef.current) {
      startInputRef.current.focus();
      startInputRef.current.select();
    }
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (editorRef.current && !editorRef.current.contains(e.target)) {
        onCancel();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onCancel]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, startValue, endValue, rippleMode]);

  // Cleanup play timeout
  useEffect(() => {
    return () => {
      if (playTimeoutRef.current) {
        clearTimeout(playTimeoutRef.current);
      }
    };
  }, []);

  const validateInputs = useCallback(() => {
    const newStart = parseTimestamp(startValue);
    const newEnd = parseTimestamp(endValue);

    if (newStart === null) {
      return { valid: false, errors: ['Invalid start time format'] };
    }
    if (newEnd === null) {
      return { valid: false, errors: ['Invalid end time format'] };
    }

    const validation = validateWordTimestamp({ start: newStart, end: newEnd });
    return validation;
  }, [startValue, endValue]);

  const handleSave = useCallback(() => {
    const validation = validateInputs();
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    const newStart = parseTimestamp(startValue);
    const newEnd = parseTimestamp(endValue);

    onSave({
      segmentIndex,
      wordIndex,
      globalIndex,
      newStart,
      newEnd,
      rippleMode
    });
  }, [startValue, endValue, rippleMode, segmentIndex, wordIndex, globalIndex, onSave, validateInputs]);

  const handlePreview = useCallback(() => {
    if (!audioRef?.current) return;

    const audio = audioRef.current;
    const newStart = parseTimestamp(startValue);
    const newEnd = parseTimestamp(endValue);

    if (newStart === null || newEnd === null) return;

    // Clear any existing timeout
    if (playTimeoutRef.current) {
      clearTimeout(playTimeoutRef.current);
    }

    // Seek to start and play
    audio.currentTime = newStart;
    audio.play();
    setIsPlaying(true);

    // Stop at end time
    const duration = (newEnd - newStart) * 1000;
    playTimeoutRef.current = setTimeout(() => {
      audio.pause();
      setIsPlaying(false);
    }, duration);
  }, [audioRef, startValue, endValue]);

  const handleStopPreview = useCallback(() => {
    if (!audioRef?.current) return;

    if (playTimeoutRef.current) {
      clearTimeout(playTimeoutRef.current);
    }

    audioRef.current.pause();
    setIsPlaying(false);
  }, [audioRef]);

  const nudgeTime = useCallback((field, amount) => {
    const currentValue = field === 'start' ? startValue : endValue;
    const parsed = parseTimestamp(currentValue);
    if (parsed === null) return;

    const newValue = Math.max(0, parsed + amount);
    const formatted = formatTimestamp(newValue);

    if (field === 'start') {
      setStartValue(formatted);
    } else {
      setEndValue(formatted);
    }

    setErrors([]);
  }, [startValue, endValue]);

  const duration = (() => {
    const start = parseTimestamp(startValue);
    const end = parseTimestamp(endValue);
    if (start === null || end === null) return null;
    return end - start;
  })();

  const validation = validateInputs();

  return createPortal(
    <div
      ref={editorRef}
      className="timestamp-editor"
      style={{ top: position.top, left: position.left }}
    >
      <div className="timestamp-editor-header">
        <span className="timestamp-editor-title">Edit Timestamp</span>
        <span className="timestamp-editor-word">"{word?.word}"</span>
      </div>

      <div className="timestamp-editor-body">
        <div className="timestamp-input-group">
          <label>Start</label>
          <div className="timestamp-input-row">
            <button
              className="nudge-btn"
              onClick={() => nudgeTime('start', -0.1)}
              title="−100ms"
            >
              ◀◀
            </button>
            <button
              className="nudge-btn"
              onClick={() => nudgeTime('start', -0.01)}
              title="−10ms"
            >
              ◀
            </button>
            <input
              ref={startInputRef}
              type="text"
              value={startValue}
              onChange={(e) => {
                setStartValue(e.target.value);
                setErrors([]);
              }}
              className={errors.some(e => e.includes('start')) ? 'error' : ''}
              placeholder="0:00.000"
            />
            <button
              className="nudge-btn"
              onClick={() => nudgeTime('start', 0.01)}
              title="+10ms"
            >
              ▶
            </button>
            <button
              className="nudge-btn"
              onClick={() => nudgeTime('start', 0.1)}
              title="+100ms"
            >
              ▶▶
            </button>
          </div>
        </div>

        <div className="timestamp-input-group">
          <label>End</label>
          <div className="timestamp-input-row">
            <button
              className="nudge-btn"
              onClick={() => nudgeTime('end', -0.1)}
              title="−100ms"
            >
              ◀◀
            </button>
            <button
              className="nudge-btn"
              onClick={() => nudgeTime('end', -0.01)}
              title="−10ms"
            >
              ◀
            </button>
            <input
              type="text"
              value={endValue}
              onChange={(e) => {
                setEndValue(e.target.value);
                setErrors([]);
              }}
              className={errors.some(e => e.includes('end')) ? 'error' : ''}
              placeholder="0:00.000"
            />
            <button
              className="nudge-btn"
              onClick={() => nudgeTime('end', 0.01)}
              title="+10ms"
            >
              ▶
            </button>
            <button
              className="nudge-btn"
              onClick={() => nudgeTime('end', 0.1)}
              title="+100ms"
            >
              ▶▶
            </button>
          </div>
        </div>

        <div className="timestamp-duration">
          Duration: {duration !== null ? `${duration.toFixed(3)}s` : '—'}
        </div>

        <div className="timestamp-ripple-options">
          <label className="ripple-label">When adjusting:</label>
          <div className="ripple-option">
            <input
              type="radio"
              id="ripple-neighbor"
              name="rippleMode"
              checked={rippleMode === RIPPLE_MODE.ADJUST_NEIGHBOR}
              onChange={() => setRippleMode(RIPPLE_MODE.ADJUST_NEIGHBOR)}
            />
            <label htmlFor="ripple-neighbor">
              Adjust neighbor word
              <span className="ripple-hint">Previous/next word adjusts to match</span>
            </label>
          </div>
          <div className="ripple-option">
            <input
              type="radio"
              id="ripple-gap"
              name="rippleMode"
              checked={rippleMode === RIPPLE_MODE.ALLOW_GAP}
              onChange={() => setRippleMode(RIPPLE_MODE.ALLOW_GAP)}
            />
            <label htmlFor="ripple-gap">
              Allow gap/overlap
              <span className="ripple-hint">Other words stay unchanged</span>
            </label>
          </div>
          <div className="ripple-option">
            <input
              type="radio"
              id="ripple-shift-before"
              name="rippleMode"
              checked={rippleMode === RIPPLE_MODE.SHIFT_ALL_BEFORE}
              onChange={() => setRippleMode(RIPPLE_MODE.SHIFT_ALL_BEFORE)}
            />
            <label htmlFor="ripple-shift-before">
              Shift all before
              <span className="ripple-hint">All preceding words shift together</span>
            </label>
          </div>
          <div className="ripple-option">
            <input
              type="radio"
              id="ripple-shift-after"
              name="rippleMode"
              checked={rippleMode === RIPPLE_MODE.SHIFT_ALL_AFTER}
              onChange={() => setRippleMode(RIPPLE_MODE.SHIFT_ALL_AFTER)}
            />
            <label htmlFor="ripple-shift-after">
              Shift all after
              <span className="ripple-hint">All following words shift together</span>
            </label>
          </div>
        </div>

        {errors.length > 0 && (
          <div className="timestamp-errors">
            {errors.map((error, i) => (
              <div key={i} className="timestamp-error">{error}</div>
            ))}
          </div>
        )}

        {previousWord && (
          <div className="timestamp-context prev-word">
            Previous: "{previousWord.word}" ends at {formatTimestamp(previousWord.end)}
          </div>
        )}
        {nextWord && (
          <div className="timestamp-context next-word">
            Next: "{nextWord.word}" starts at {formatTimestamp(nextWord.start)}
          </div>
        )}
      </div>

      <div className="timestamp-editor-footer">
        <button
          className={`preview-btn ${isPlaying ? 'playing' : ''}`}
          onClick={isPlaying ? handleStopPreview : handlePreview}
          disabled={!validation.valid}
        >
          {isPlaying ? '⏹ Stop' : '▶ Preview'}
        </button>
        <div className="timestamp-editor-actions">
          <button className="cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="save-btn"
            onClick={handleSave}
            disabled={!validation.valid}
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TimestampEditor;
